// ============================================================
// تاریخ شمسی از زبان طبیعی
//
// کاربر می‌گوید «گزارش دیروز» یا «از اول خرداد تا امروز» — این فایل
// آن را به بازه‌ی «YYYY/MM/DD» شمسی تبدیل می‌کند، همان قالبی که
// /monitoring/reports/flour-distribution انتظار دارد.
//
// از DateObject همان کتابخانه‌ای استفاده می‌شود که صفحه‌ی گزارش‌ها
// استفاده می‌کند تا تبدیل تقویم در دو جای برنامه یکسان بماند.
// ============================================================

import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { hasAny, normalize, toEnglishDigits } from "./text";

export interface DateRange {
    start: string; // YYYY/MM/DD شمسی
    end: string;
    /** توضیح خوانا برای نشان دادن به کاربر */
    label: string;
}

const jalali = () => new DateObject({ calendar: persian, locale: persian_fa });

/** قالب استاندارد با ارقام انگلیسی — API با ارقام فارسی کار نمی‌کند. */
const fmt = (d: DateObject): string => toEnglishDigits(d.format("YYYY/MM/DD"));

export const todayJalali = (): string => fmt(jalali());

const shiftDays = (days: number): DateObject => {
    const d = jalali();
    return days === 0 ? d : d.add(days, "day");
};

const MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];

/** «۱۴۰۴/۰۳/۱۲»، «1404-3-12»، «1404.03.12» */
const EXPLICIT = /(1[34]\d{2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{1,2})/;
/** «۱۲ خرداد ۱۴۰۴» یا «۱۲ خرداد» (سال جاری) */
const BY_MONTH_NAME = new RegExp("(\\d{1,2})\\s+(" + MONTHS.join("|") + ")(?:\\s+(1[34]\\d{2}))?");

/**
 * روزهای ماه به صورت ترتیبی: «از تاریخ سی‌ام تا سی و یکم».
 * ماه و سال از تاریخ امروز گرفته می‌شود، چون کاربر هم همین را
 * منظور دارد وقتی ماه را نمی‌گوید.
 *
 * بلندترها اول می‌آیند: «بیست و یکم» باید قبل از «یکم» چک شود،
 * وگرنه «بیست و یکم» به روز ۱ تبدیل می‌شد.
 */
const ORDINAL_DAYS: Array<[string, number]> = [
    ["بیست و یکم", 21], ["بیست و دوم", 22], ["بیست و سوم", 23], ["بیست و چهارم", 24],
    ["بیست و پنجم", 25], ["بیست و ششم", 26], ["بیست و هفتم", 27], ["بیست و هشتم", 28],
    ["بیست و نهم", 29], ["سی و یکم", 31], ["سی ام", 30], ["سیام", 30],
    ["یازدهم", 11], ["دوازدهم", 12], ["سیزدهم", 13], ["چهاردهم", 14],
    ["پانزدهم", 15], ["پونزدهم", 15], ["شانزدهم", 16], ["هفدهم", 17],
    ["هجدهم", 18], ["نوزدهم", 19], ["بیستم", 20],
    ["چهارم", 4], ["پنجم", 5], ["ششم", 6], ["هفتم", 7], ["هشتم", 8],
    ["نهم", 9], ["دهم", 10], ["سوم", 3], ["دوم", 2], ["یکم", 1], ["اول", 1],
];

const pad = (n: number) => (n < 10 ? "0" + n : String(n));

const isValidJalali = (y: number, m: number, d: number) =>
    y >= 1300 && y <= 1499 && m >= 1 && m <= 12 && d >= 1 && d <= 31;

/** یک تاریخ مشخص را از جمله بیرون می‌کشد. null یعنی تاریخی نبود. */
export const parseSingleDate = (text: string): string | null => {
    const source = toEnglishDigits(text);

    const explicit = source.match(EXPLICIT);
    if (explicit) {
        const y = Number(explicit[1]);
        const m = Number(explicit[2]);
        const d = Number(explicit[3]);
        if (isValidJalali(y, m, d)) return y + "/" + pad(m) + "/" + pad(d);
    }

    const byName = normalize(source).match(BY_MONTH_NAME);
    if (byName) {
        const d = Number(byName[1]);
        const m = MONTHS.map((x) => normalize(x)).indexOf(byName[2]) + 1;
        const y = byName[3] ? Number(byName[3]) : Number(todayJalali().slice(0, 4));
        if (isValidJalali(y, m, d)) return y + "/" + pad(m) + "/" + pad(d);
    }

    // بدون \b نوشته شده‌اند: در جاوااسکریپت \b فقط مرز [A-Za-z0-9_] را
    // می‌شناسد، پس /\bامروز\b/ هیچ‌وقت با متن فارسی جور نمی‌شود.
    const n = normalize(source);
    if (hasAny(n, ["امروز", "همین امروز", "امشب"])) return fmt(shiftDays(0));
    if (hasAny(n, ["دیروز", "روز گذشته", "روز قبل", "دیشب"])) return fmt(shiftDays(-1));
    if (hasAny(n, ["پریروز", "پری روز"])) return fmt(shiftDays(-2));
    if (hasAny(n, ["فردا"])) return fmt(shiftDays(1));

    // روز ترتیبی، با ماه و سال جاری
    for (let i = 0; i < ORDINAL_DAYS.length; i++) {
        const word = ORDINAL_DAYS[i][0];
        const day = ORDINAL_DAYS[i][1];
        if (n.indexOf(word) !== -1) {
            const today = todayJalali();
            const year = Number(today.slice(0, 4));
            const month = Number(today.slice(5, 7));
            if (isValidJalali(year, month, day)) {
                return year + "/" + pad(month) + "/" + pad(day);
            }
        }
    }

    return null;
};

/**
 * تعداد سطر درخواستی: «۵۰ سطر نشون بده»، «۲۰ تا»، «تعداد سطر ۱۰۰».
 *
 * عمداً بعد از حذف تاریخ صدا زده می‌شود، وگرنه «۱۴۰۴» را تعداد سطر
 * می‌فهمید. سقف ۵۰۰ گذاشته شده تا یک اشتباه تایپی، هزار رکورد را
 * روی سر مرورگر خالی نکند.
 */
export const parseRowCount = (text: string): number | null => {
    const source = normalize(stripDateExpressions(text));
    const match = source.match(/(\d{1,4})\s*(?:سطر|ردیف|تا|رکورد|مورد|عدد)/);
    const labeled = source.match(/(?:تعداد|سطر|ردیف)\s*(\d{1,4})/);

    const raw = match ? match[1] : labeled ? labeled[1] : null;
    if (!raw) return null;

    const count = Number(raw);
    if (!count || count < 1) return null;
    return Math.min(count, 500);
};

/**
 * بازه‌ی تاریخ را از جمله بیرون می‌کشد.
 *
 * ترتیب بررسی مهم است: «از ... تا ...» باید قبل از تاریخ تکی بیاید،
 * وگرنه فقط تاریخ اول دیده می‌شود و بازه به یک روز تبدیل می‌شود.
 */
export const parseDateRange = (text: string): DateRange | null => {
    const source = toEnglishDigits(text);
    const n = normalize(source);

    // ---- «از X تا Y» ----
    const between = source.match(
        /از\s*(.+?)\s*(?:تا|الی|لغایت)\s*(.+)/
    );
    if (between) {
        const start = parseSingleDate(between[1]);
        const end = parseSingleDate(between[2]);
        if (start && end) {
            return { start, end, label: "از " + start + " تا " + end };
        }
    }

    // ---- دو تاریخ صریح پشت سر هم ----
    const all: string[] = [];
    const globalExplicit = new RegExp(EXPLICIT.source, "g");
    let hit: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((hit = globalExplicit.exec(source)) !== null) {
        const y = Number(hit[1]);
        const m = Number(hit[2]);
        const d = Number(hit[3]);
        if (isValidJalali(y, m, d)) all.push(y + "/" + pad(m) + "/" + pad(d));
    }
    if (all.length >= 2) {
        return { start: all[0], end: all[1], label: "از " + all[0] + " تا " + all[1] };
    }

    // ---- بازه‌های نسبی ----
    // ترتیب مهم است: «هفته گذشته» قبل از «این هفته» نه، ولی هر دو
    // قبل از تاریخ تکی؛ و مشخص‌تر («ماه گذشته») قبل از عام‌تر.
    if (hasAny(n, ["هفته گذشته", "هفته پیش", "هفته قبل"])) {
        const back = jalali().weekDay.index; // شنبه = ۰ در تقویم فارسی
        return {
            start: fmt(shiftDays(-back - 7)),
            end: fmt(shiftDays(-back - 1)),
            label: "هفته گذشته",
        };
    }
    if (hasAny(n, ["این هفته", "هفته جاری"])) {
        const back = jalali().weekDay.index;
        return {
            start: fmt(shiftDays(-back)),
            end: fmt(jalali()),
            label: "این هفته",
        };
    }
    const lastNDays = n.match(/(\d{1,2}) روز (?:گذشته|اخیر|قبل)/);
    if (lastNDays) {
        const days = Math.max(1, Number(lastNDays[1]));
        return {
            start: fmt(shiftDays(-days + 1)),
            end: fmt(jalali()),
            label: days + " روز گذشته",
        };
    }
    if (hasAny(n, ["ماه گذشته", "ماه پیش", "ماه قبل"])) {
        const first = fmt(jalali().toFirstOfMonth().add(-1, "month"));
        const last = fmt(jalali().toFirstOfMonth().add(-1, "month").toLastOfMonth());
        return { start: first, end: last, label: "ماه گذشته" };
    }
    if (hasAny(n, ["این ماه", "ماه جاری"])) {
        return { start: fmt(jalali().toFirstOfMonth()), end: fmt(jalali()), label: "این ماه" };
    }
    if (hasAny(n, ["امسال", "سال جاری"])) {
        return { start: fmt(jalali().toFirstOfYear()), end: fmt(jalali()), label: "امسال" };
    }

    // ---- تاریخ تکی → بازه‌ی یک‌روزه ----
    const single = parseSingleDate(source);
    if (single) return { start: single, end: single, label: single };

    return null;
};

/**
 * عبارت‌های تاریخ را از جمله حذف می‌کند.
 *
 * لازم است چون «باقی‌مانده‌ی جمله» به عنوان عبارت جست‌وجو استفاده
 * می‌شود؛ بدون این، «در تاریخ ۱۴۰۵/۰۶/۳۰ راننده رضا محمدی» به
 * جست‌وجوی «1405 06 30 رضا محمدی» تبدیل می‌شد و هیچ‌وقت چیزی
 * پیدا نمی‌کرد.
 */
export const stripDateExpressions = (text: string): string => {
    // ترتیب مهم است: تاریخ‌های رقمی *قبل از* normalize حذف می‌شوند،
    // چون normalize اسلش و خط تیره را به فاصله تبدیل می‌کند و بعد از
    // آن «1404/03/12» دیگر قابل تشخیص نیست.
    let result = toEnglishDigits(text)
        .replace(/از\s*1[34]\d{2}\s*[/\-.]\s*\d{1,2}\s*[/\-.]\s*\d{1,2}/g, " ")
        .replace(/(?:تا|الی|لغایت)\s*1[34]\d{2}\s*[/\-.]\s*\d{1,2}\s*[/\-.]\s*\d{1,2}/g, " ")
        .replace(new RegExp(EXPLICIT.source, "g"), " ")
        .replace(new RegExp("(\\d{1,2})\\s+(" + MONTHS.join("|") + ")(\\s+1[34]\\d{2})?", "g"), " ")
        .replace(/\d{1,2} روز (?:گذشته|اخیر|قبل)/g, " ");

    // حالا یکسان‌سازی، تا «سی‌ام» با نیم‌فاصله هم شناخته شود
    result = normalize(result);

    const words = [
        "در تاریخ", "از تاریخ", "تا تاریخ", "تاریخ", "امروز", "دیروز", "پریروز",
        "فردا", "امشب", "دیشب", "این هفته", "هفته جاری", "هفته گذشته",
        "هفته پیش", "هفته قبل", "این ماه", "ماه جاری", "ماه گذشته", "ماه پیش",
        "ماه قبل", "روز گذشته", "روز قبل", "امسال", "سال جاری",
    ].concat(ORDINAL_DAYS.map((pair) => pair[0]));

    words.forEach((word) => {
        result = result.split(word).join(" ");
    });

    return result.replace(/\s+/g, " ").trim();
};
